import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Hero from "./components/Hero";
import CTASection from "./components/CTASection";
import CategoryGrid from "./components/CategoryGrid";
import AboutSection from "./components/AboutSection";
import ArticleCards from "./components/ArticleCards";
import TestimonialsSection from "./components/TestimonialsSection";
import { SITE_URL } from "@/constants";

export default function Home() {
  const siteUrl = SITE_URL;

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Kakobuy Spreadsheet Polska",
    "url": siteUrl,
    "description": "Najlepszy polski katalog produktów Kakobuy. Codziennie aktualizowany spreadsheet z tysiącami produktów w 14 kategoriach.",
    "foundingDate": "2025",
    "logo": siteUrl + "/favicon.png",
    "sameAs": [
      "https://kaspreadsheet.com/"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "url": siteUrl
    }
  };

  const webPageJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "url": siteUrl + "/",
    "name": "Kakobuy Spreadsheet 2026 | Najlepszy Polski Katalog Produktów",
    "description": "Odkryj najlepszy Kakobuy Spreadsheet 2026. Kompletny katalog produktów: buty, ubrania, akcesoria. Sprawdź bezpieczeństwo, poradniki zakupowe i najnowsze aktualizacje. Polska społeczność Kakobuy.",
    "inLanguage": "pl",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Kakobuy Spreadsheet Polska",
      "url": siteUrl
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [{
        "@type": "ListItem",
        "position": 1,
        "name": "Strona Główna",
        "item": siteUrl + "/"
      }]
    }
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Czym jest Kakobuy Spreadsheet?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Kakobuy Spreadsheet to codziennie aktualizowany katalog produktów dostępnych na platformie Kakobuy. Zawiera posegregowane kategorie, opisy i linki do produktów, dzięki czemu możesz szybko znaleźć to, czego szukasz, bez konieczności przeszukiwania całej platformy."
        }
      },
      {
        "@type": "Question",
        "name": "Czy Kakobuy jest bezpieczny?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Tak, Kakobuy to legalna i bezpieczna platforma zakupowa. Oferuje system QC (kontroli jakości), bezpieczne metody płatności oraz wsparcie klienta. Nasz spreadsheet pomaga dodatkowo weryfikować produkty przed zakupem, co zwiększa Twoje bezpieczeństwo."
        }
      },
      {
        "@type": "Question",
        "name": "Jak często aktualizowany jest spreadsheet?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Nasz Kakobuy Spreadsheet jest aktualizowany codziennie. Każdego dnia dodajemy nowe produkty, usuwamy niedostępne i aktualizujemy ceny, abyś zawsze miał dostęp do najbardziej aktualnych informacji."
        }
      },
      {
        "@type": "Question",
        "name": "Ile kosztuje dostęp do spreadsheeta?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Kakobuy Spreadsheet jest całkowicie darmowy! Naszym celem jest pomoc polskiej społeczności w znalezieniu najlepszych produktów. Nie pobieramy żadnych opłat za dostęp do katalogu."
        }
      }
    ]
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <Navbar />
      <main>
        <Hero />
        <CTASection />
        <CategoryGrid />
        <AboutSection />
        <ArticleCards />
        <TestimonialsSection />

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
              FAQ - Najczęściej Zadawane Pytania
            </h2>
            <p className="text-sm text-foreground-600 mb-10">
              Odpowiadamy na najważniejsze pytania dotyczące Kakobuy Spreadsheet
            </p>

            <div className="space-y-4 text-left">
              <details className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
                <summary className="flex items-center justify-between cursor-pointer p-5 md:p-6 text-foreground-900 font-semibold text-sm md:text-base">
                  Czym jest Kakobuy Spreadsheet?
                  <i className="ri-arrow-down-s-line text-lg group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 md:px-6 pb-5 text-sm text-foreground-600 leading-relaxed">
                  Kakobuy Spreadsheet to codziennie aktualizowany katalog produktów dostępnych na platformie Kakobuy. Zawiera posegregowane kategorie, opisy i linki do produktów, dzięki czemu możesz szybko znaleźć to, czego szukasz, bez konieczności przeszukiwania całej platformy.
                </div>
              </details>

              <details className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
                <summary className="flex items-center justify-between cursor-pointer p-5 md:p-6 text-foreground-900 font-semibold text-sm md:text-base">
                  Czy Kakobuy jest bezpieczny?
                  <i className="ri-arrow-down-s-line text-lg group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 md:px-6 pb-5 text-sm text-foreground-600 leading-relaxed">
                  Tak, Kakobuy to legalna i bezpieczna platforma zakupowa. Oferuje system QC (kontroli jakości), bezpieczne metody płatności oraz wsparcie klienta. Nasz spreadsheet pomaga dodatkowo weryfikować produkty przed zakupem, co zwiększa Twoje bezpieczeństwo.
                </div>
              </details>

              <details className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
                <summary className="flex items-center justify-between cursor-pointer p-5 md:p-6 text-foreground-900 font-semibold text-sm md:text-base">
                  Jak często aktualizowany jest spreadsheet?
                  <i className="ri-arrow-down-s-line text-lg group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 md:px-6 pb-5 text-sm text-foreground-600 leading-relaxed">
                  Nasz Kakobuy Spreadsheet jest aktualizowany codziennie. Każdego dnia dodajemy nowe produkty, usuwamy niedostępne i aktualizujemy ceny, abyś zawsze miał dostęp do najbardziej aktualnych informacji.
                </div>
              </details>

              <details className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
                <summary className="flex items-center justify-between cursor-pointer p-5 md:p-6 text-foreground-900 font-semibold text-sm md:text-base">
                  Ile kosztuje dostęp do spreadsheeta?
                  <i className="ri-arrow-down-s-line text-lg group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 md:px-6 pb-5 text-sm text-foreground-600 leading-relaxed">
                  Kakobuy Spreadsheet jest całkowicie darmowy! Naszym celem jest pomoc polskiej społeczności w znalezieniu najlepszych produktów. Nie pobieramy żadnych opłat za dostęp do katalogu.
                </div>
              </details>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}